import {
	resolveTableViewState,
	Table,
	TableCard,
	TableRowActions,
	TableStateRow,
} from "@components";
import type { ReactNode } from "react";
import { cx } from "@/utils/cx";
import {
	INFRACTION_DESCRIPTION_MAX_LENGTH,
	INFRACTION_TABLE_COLUMNS,
} from "../constants/infraction-table-columns.constant";
import type { InfractionListItem } from "../types/infraction.types";
import { truncateText } from "../utils/formatters";
import {
	getListItemStatusLabel,
	getVehicleLabel,
} from "../utils/infraction-list-display";
import { InfractionDeadlineCell } from "./infraction-deadline-cell";
import { InfractionRowActionsMenu } from "./infraction-row-actions-menu";
import { InfractionStatusBadge } from "./infraction-status-badge";

type InfractionsTableProps = {
	items: InfractionListItem[];
	isLoading?: boolean;
	isError?: boolean;
	onRetry?: () => void;
	footer?: ReactNode;
	onRowClick: (id: string) => void;
	onEdit: (id: string) => void;
	onGenerateGuide: (id: string) => void;
};

function InfractionsTableHead({
	column,
}: {
	column: (typeof INFRACTION_TABLE_COLUMNS)[number];
}) {
	return (
		<Table.Head
			id={column.id}
			isRowHeader={column.id === "deadline"}
			key={column.id}
			label={column.label}
			style={{
				...(!!column?.minWidth && {
					minWidth: `${column.minWidth}px`,
				}),
			}}
		/>
	);
}

function InfractionTableSelectableCell({
	children,
	className,
	onOpenDetail,
	rowLabel,
	isRowFocusTarget = false,
}: {
	children: ReactNode;
	className?: string;
	onOpenDetail: () => void;
	rowLabel: string;
	isRowFocusTarget?: boolean;
}) {
	return (
		<Table.Cell className={cx("relative", className)}>
			<button
				aria-label={`Abrir detalhes da infração ${rowLabel}`}
				className="absolute inset-0 z-0 cursor-pointer border-0 bg-transparent p-0"
				onClick={onOpenDetail}
				onKeyDown={(event) => {
					if (event.key === "Enter" || event.key === " ") {
						event.preventDefault();
						onOpenDetail();
					}
				}}
				tabIndex={isRowFocusTarget ? 0 : -1}
				type="button"
			/>
			<div className="relative z-0 w-fit max-w-full select-text">
				{children}
			</div>
		</Table.Cell>
	);
}

function InfractionsTableRow({
	item,
	onRowClick,
	onEdit,
	onGenerateGuide,
}: {
	item: InfractionListItem;
	onRowClick: (id: string) => void;
	onEdit: (id: string) => void;
	onGenerateGuide: (id: string) => void;
}) {
	const rowId = String(item.id);
	const vehicleLabel = getVehicleLabel(item.vehicleBrand, item.vehicleModel);
	const statusLabel = getListItemStatusLabel(item);
	const rowLabel = item.plate ?? item.ait ?? rowId;

	const handleOpenDetail = () => {
		onRowClick(rowId);
	};

	return (
		<Table.Row
			className="last:border-b-0 hover:bg-secondary_subtle"
			columns={INFRACTION_TABLE_COLUMNS}
			id={rowId}
		>
			<InfractionTableSelectableCell
				isRowFocusTarget
				onOpenDetail={handleOpenDetail}
				rowLabel={rowLabel}
			>
				<InfractionDeadlineCell item={item} />
			</InfractionTableSelectableCell>
			<InfractionTableSelectableCell
				className="whitespace-nowrap"
				onOpenDetail={handleOpenDetail}
				rowLabel={rowLabel}
			>
				<span className="font-medium">{item.plate ?? "—"}</span>
				{vehicleLabel !== "—" ? (
					<span className="block text-tertiary text-xs leading-4">
						{vehicleLabel}
					</span>
				) : null}
			</InfractionTableSelectableCell>
			<InfractionTableSelectableCell
				className="whitespace-nowrap"
				onOpenDetail={handleOpenDetail}
				rowLabel={rowLabel}
			>
				{item.driverName ?? "—"}
			</InfractionTableSelectableCell>
			<InfractionTableSelectableCell
				onOpenDetail={handleOpenDetail}
				rowLabel={rowLabel}
			>
				<InfractionStatusBadge
					displayStatus={item.displayStatus}
					label={statusLabel}
				/>
			</InfractionTableSelectableCell>
			<InfractionTableSelectableCell
				className="max-w-[280px] text-tertiary"
				onOpenDetail={handleOpenDetail}
				rowLabel={rowLabel}
			>
				{truncateText(
					item.description ?? "—",
					INFRACTION_DESCRIPTION_MAX_LENGTH
				)}
			</InfractionTableSelectableCell>
			<InfractionTableSelectableCell
				className="whitespace-nowrap font-mono"
				onOpenDetail={handleOpenDetail}
				rowLabel={rowLabel}
			>
				{item.ait ?? "—"}
			</InfractionTableSelectableCell>
			<Table.Cell>
				<TableRowActions label={`Ações da infração ${rowLabel}`}>
					<InfractionRowActionsMenu
						item={item}
						onEdit={onEdit}
						onGenerateGuide={onGenerateGuide}
					/>
				</TableRowActions>
			</Table.Cell>
		</Table.Row>
	);
}

export function InfractionsTable({
	items,
	isLoading = false,
	isError = false,
	onRetry,
	footer,
	onRowClick,
	onEdit,
	onGenerateGuide,
}: InfractionsTableProps) {
	const view = resolveTableViewState({
		itemCount: items.length,
		isLoading,
		isError,
	});

	return (
		<TableCard.Root data-slot="infractions-table" variant="listing">
			<Table
				aria-label="Listagem de infrações"
				isRefreshing={view.isRefreshing}
				refreshingLabel="Atualizando infrações"
			>
				<Table.Header columns={INFRACTION_TABLE_COLUMNS}>
					{INFRACTION_TABLE_COLUMNS.map((column) => (
						<InfractionsTableHead column={column} key={column.id} />
					))}
				</Table.Header>
				<Table.Body>
					{view.view === "data" ? (
						<>
							{view.hasRefreshError ? (
								<TableStateRow
									colSpan={INFRACTION_TABLE_COLUMNS.length}
									onRetry={onRetry}
									state="refresh-error"
								/>
							) : null}
							{items.map((item) => (
								<InfractionsTableRow
									item={item}
									key={item.id}
									onEdit={onEdit}
									onGenerateGuide={onGenerateGuide}
									onRowClick={onRowClick}
								/>
							))}
						</>
					) : (
						<TableStateRow
							colSpan={INFRACTION_TABLE_COLUMNS.length}
							emptyContent="Nenhuma infração encontrada para os filtros selecionados."
							errorMessage="Não foi possível carregar as infrações."
							loadingLabel="Carregando infrações"
							onRetry={onRetry}
							state={view.view}
						/>
					)}
				</Table.Body>
			</Table>
			{footer}
		</TableCard.Root>
	);
}
